// Main application router with protected routes

import React, { ReactNode, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useAuth } from '@contexts/AuthContext';
// Auth pages
import LoginPage from '@components/features/Auth/LoginPage';

// Layout
import Layout from '@components/common/Layout/Layout';

// Pages
import DashboardPage from '@components/features/Dashboard/DashboardPage';
import ProductsPage from '@components/features/Products/ProductsPage';
import OrdersPage from '@components/features/Orders/OrdersPage';
import AnalyticsPage from '@components/features/Analytics/AnalyticsPage';
import UsersPage from '@components/features/Users/UsersPage';
import SettingsPage from '@components/features/Settings/SettingsPage';

// Loading component
function LoadingScreen() {
  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="100vh"
    >
      <CircularProgress size={60} />
    </Box>
  );
}

// Protected route wrapper
interface ProtectedRouteProps {
  children: ReactNode;
  requiredRole?: string;
}

function ProtectedRoute({ children, requiredRole }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, user } = useAuth();

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (requiredRole && user?.role !== requiredRole) {
    return <Navigate to="/unauthorized" replace />;
  }

  return <>{children}</>;
}


function UnauthorizedPage() {
  return (
    <Box 
      display="flex" 
      flexDirection="column" 
      alignItems="center" 
      justifyContent="center" 
      minHeight="100vh"
      textAlign="center"
      p={3}
    >
      <h1>Brak uprawnień</h1>
      <p>Nie masz uprawnień do tej strony.</p>
    </Box>
  );
}

export default function AppRouter() {
  const { isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  console.log('AppRouter: isAuthenticated:', isAuthenticated, 'isLoading:', isLoading, 'location:', location.pathname);

  // Redirect authenticated users away from login page
  useEffect(() => {
    if (isAuthenticated && location.pathname === '/login') {
      console.log('AppRouter: User is authenticated, redirecting from login to dashboard');
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, location.pathname, navigate]);

  if (isLoading) {
    return <LoadingScreen />;
  }

  return (
    <React.Suspense fallback={<LoadingScreen />}>
      <Routes>
        {/* Public routes */}
        <Route 
          path="/login" 
          element={
            isAuthenticated ? <Navigate to="/" replace /> : <LoginPage />
          } 
        />
        
        {/* Protected routes with Layout */}
        <Route 
          path="/" 
          element={
            <ProtectedRoute>
              <Layout>
                <DashboardPage />
              </Layout>
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/products" 
          element={
            <ProtectedRoute>
              <Layout>
                <ProductsPage />
              </Layout>
            </ProtectedRoute>
          } 
        />

        <Route 
          path="/orders" 
          element={
            <ProtectedRoute>
              <Layout>
                <OrdersPage />
              </Layout>
            </ProtectedRoute>
          } 
        />

        <Route 
          path="/analytics" 
          element={
            <ProtectedRoute>
              <Layout>
                <AnalyticsPage />
              </Layout>
            </ProtectedRoute>
          } 
        />

        <Route 
          path="/users" 
          element={
            <ProtectedRoute requiredRole="admin">
              <Layout>
                <UsersPage />
              </Layout>
            </ProtectedRoute>
          } 
        />

        <Route 
          path="/settings" 
          element={
            <ProtectedRoute>
              <Layout>
                <SettingsPage />
              </Layout>
            </ProtectedRoute>
          } 
        />
        
        {/* Unauthorized page */}
        <Route path="/unauthorized" element={<UnauthorizedPage />} />
        
        {/* Catch-all redirect */}
        <Route 
          path="*" 
          element={
            isAuthenticated ? <Navigate to="/" replace /> : <Navigate to="/login" replace />
          } 
        />
      </Routes>
    </React.Suspense>
  );
}